import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { useToast } from '../contexts/ToastContext'
import { useAuth } from '../contexts/AuthContextRedux'
import { fetchChatHistory, cleanupDuplicateSessions } from '../actions/chatActions'

export default function ChatHistorySidebar({ isOpen, onClose, onNewChat }) {
  const [page, setPage] = useState(1)
  const navigate = useNavigate()
  const { addToast } = useToast()
  const dispatch = useDispatch()
  const { user } = useAuth()
  const { activeSession, sessions, loading } = useSelector(state => state.chat)

  // Convert sessions object to array for display, filter by current user and remove duplicates
  const history = Object.values(sessions)
    .filter(session => {
      // If session has user_id, filter by it; otherwise show all (for backward compatibility)
      return !session.user_id || session.user_id === user?.id || session.user_email === user?.email
    })
    .reduce((unique, session) => {
      // Remove duplicates based on session title and created date
      const isDuplicate = unique.some(existing => 
        existing.title === session.title && 
        Math.abs(new Date(existing.createdAt) - new Date(session.createdAt)) < 60000 // Within 1 minute
      )
      if (!isDuplicate) {
        unique.push(session)
      } else {
        console.log('🔄 Removing duplicate session:', session.title, session.id)
      }
      return unique
    }, [])
    .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))

  useEffect(() => {
    if (isOpen) {
      if (history.length === 0) {
        dispatch(fetchChatHistory())
      } else {
        // Clean up duplicates when sidebar opens
        dispatch(cleanupDuplicateSessions())
      }
    }
  }, [isOpen, dispatch, history.length])

  const loadHistory = async () => {
    try {
      dispatch(fetchChatHistory())
    } catch (error) {
      addToast('Failed to load history', 'error')
    }
  }

  const handleItemClick = (answerId) => {
    navigate(`/chat/${answerId}`)
    onClose()
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now - date
    const diffMins = Math.floor(diffMs / 60000)
    
    if (diffMins < 60) {
      return `${diffMins} minutes ago`
    } else if (diffMins < 1440) {
      const hours = Math.floor(diffMins / 60)
      return `${hours} hour${hours > 1 ? 's' : ''} ago`
    } else {
      return date.toLocaleDateString()
    }
  }

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <div className={`
        fixed top-0 left-0 h-full w-80 bg-base-100 shadow-lg transform transition-transform duration-300 ease-in-out z-50
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <h2 className="text-lg font-semibold">Chat History</h2>
            <div className="flex items-center space-x-2">
              <button
                onClick={onNewChat}
                className="btn btn-sm btn-primary"
                title="New Chat"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
              <button
                onClick={onClose}
                className="btn btn-sm btn-ghost"
                title="Close"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          
          {/* History List */}
          <div className="flex-1 overflow-auto p-4">
            {loading ? (
              <div className="flex justify-center py-4">
                <span className="loading loading-spinner loading-md"></span>
              </div>
            ) : history.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-base-content/60 mb-4">No history yet</p>
                <button
                  onClick={loadHistory}
                  className="btn btn-sm btn-primary"
                >
                  Refresh
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {history.map((item, index) => (
                  <div
                    key={item.id || `session-${index}`}
                    className={`
                      card bg-base-200 cursor-pointer hover:bg-base-300 transition-colors
                      ${activeSession === item.id ? 'ring-2 ring-primary' : ''}
                    `}
                    onClick={() => handleItemClick(item.id)}
                  >
                    <div className="card-body p-3">
                      <h4 className="font-semibold text-sm line-clamp-2">
                        {item.title || 'Untitled Research'}
                      </h4>
                      <p className="text-xs text-base-content/60">
                        {formatDate(item.updatedAt || item.createdAt)}
                      </p>
                    </div>
                  </div>
                ))}
                
                <button
                  className="btn btn-sm btn-ghost w-full mt-4"
                  onClick={loadHistory}
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'Refresh'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}